
import { defineComponent, onMounted, onUnmounted, nextTick, ref, watch, watchEffect, computed } from 'vue';
import { Apollo, Notify } from '@/core/services';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { GET_ORDER_DETAIL, RESEND_EMAIL, GET_ORDER_STATUS } from '../graphql/Queries';
import { UPDATE_ORDER_STATUS } from '../graphql/Mutations';
import { useRoute } from 'vue-router';
import { setPageHistory } from '@/core/helpers/toolbar';
import Table from '../../../components/Table/Table.vue';
import { generateAvatar } from '@/core/helpers/generateAvatar';
import InnerLoader from '../../../components/InnerLoader.vue';
import { Select } from '@/components/input-elements';
import { asideTheme } from '@/core/helpers/config';
import Axios from '@/core/services/Http';
export default defineComponent({
    name: 'order',
    components: { Table, InnerLoader, Select },

    setup() {
        const route = useRoute();
        const i18n = useI18n();
        const store = useStore();
        const loader = ref(false);
        const orders = ref([]) as Record<any, any>;
        const customer = ref({}) as Record<any, any>;
        const orderTotals = ref({}) as Record<any, any>;
        const system_locale = ref();
        const sendMailLoader = ref(false);
        const order_status: any = ref([]);
        const statusList = ref([]) as Record<any, any>;
        const orderStatusList = ref([]) as Record<any, any>;
        const defaultImage = ref(store?.state?.AuthModule?.default_image);

        const columns = ref([
            {
                label: '',
                key: 'image'
            },
            {
                label: 'message.PRODUCT',
                key: 'description'
            },
            {
                label: 'message.QUANTITY',
                key: 'quantity'
            },
            {
                label: 'message.PRICE',
                key: 'price',
                textAlignment: 'end'
            },
            {
                label: 'message.TOTAL',
                key: 'total',
                textAlignment: 'end'
            }
        ]);

        watchEffect(() => {
            system_locale.value = i18n.fallbackLocale.value;
            statusList.value = [];
            orderStatusList.value.forEach((element, index) => {
                if (element.id == 1 || element.id == 3 || element.id == 5 || element.id == 6 || element.id == 10) {
                    statusList.value.push({
                        label: JSON.parse(element?.status)[system_locale.value]?.name,
                        value: element.id
                    });
                }
            });
        });

        const me = computed(() => {
            return store.getters.getUser;
        });

        const companyLogo = name => {
            return generateAvatar(name);
        };

        const tryParseJson = (value: string) => {
            const trimmed = value.trim();
            if (!trimmed) {
                return trimmed;
            }

            try {
                return JSON.parse(trimmed);
            } catch {
                const jsonStart = trimmed.search(/[{[]/);
                if (jsonStart === -1) {
                    return value;
                }

                const candidate = trimmed.slice(jsonStart);
                try {
                    return JSON.parse(candidate);
                } catch {
                    const lastBrace = Math.max(candidate.lastIndexOf('}'), candidate.lastIndexOf(']'));
                    if (lastBrace > 0) {
                        try {
                            return JSON.parse(candidate.slice(0, lastBrace + 1));
                        } catch {
                            return value;
                        }
                    }
                    return value;
                }
            }
        };

        const decodePayload = (value: any, depth = 0): any => {
            if (depth > 8 || value == null) {
                return value;
            }

            if (typeof value === 'string') {
                const parsed = tryParseJson(value);
                if (parsed !== value && typeof parsed === 'object') {
                    return decodePayload(parsed, depth + 1);
                }
                return value;
            }

            return value;
        };

        // Technical errors shown as a short label instead of the raw request URI details.
        const genericMessagePatterns = [/no http resource was found that matches the request uri/i];

        const isGenericMessage = (text: string) => {
            return genericMessagePatterns.some(pattern => pattern.test(text));
        };

        const cleanMessage = (text: string) => {
            return String(text)
                .replace(/\s*CorrelationId:\s*[0-9a-f-]+\.?/gi, '')
                .replace(/^(Business Central|SAP|e-conomic|Economic)\s+API\s+(POST|GET|PUT|PATCH|DELETE)\s+error\s+\d+:\s*/i, '')
                .replace(/^error\s+\d+:\s*/i, '')
                .trim();
        };

        const extractMeaningfulText = (value: any, depth = 0): string => {
            if (value == null || value === '' || depth > 10) {
                return '';
            }

            const decoded = decodePayload(value);

            if (typeof decoded === 'boolean' || typeof decoded === 'number') {
                return '';
            }

            if (typeof decoded === 'string') {
                const jsonStart = decoded.search(/[{[]/);
                if (jsonStart !== -1) {
                    const nested = tryParseJson(decoded.slice(jsonStart));
                    if (typeof nested === 'object' && nested !== null) {
                        const nestedText = extractMeaningfulText(nested, depth + 1);
                        if (nestedText) {
                            return nestedText;
                        }
                    }
                }

                const cleaned = cleanMessage(decoded);
                if (!cleaned || ['true', 'false', 'null', 'undefined', 'success', 'error'].includes(cleaned.toLowerCase())) {
                    return '';
                }

                return cleaned;
            }

            if (Array.isArray(decoded)) {
                return decoded
                    .map(item => extractMeaningfulText(item, depth + 1))
                    .filter(Boolean)
                    .join('\n');
            }

            if (typeof decoded !== 'object') {
                return '';
            }

            const preferredKeys = ['message', 'error', 'error_message', 'errorMessage', 'detail', 'details', 'description', 'reason', 'title', 'innererror', 'innerError'];
            for (const key of preferredKeys) {
                if (decoded[key] != null && decoded[key] !== '') {
                    const text = extractMeaningfulText(decoded[key], depth + 1);
                    if (text) {
                        return text;
                    }
                }
            }

            const skipKeys = new Set(['code', 'target', 'success', 'status', 'statusCode', 'status_code', 'correlationId', 'CorrelationId', 'traceId', 'type']);
            const collected: string[] = [];
            Object.entries(decoded).forEach(([key, nestedValue]) => {
                if (skipKeys.has(key)) {
                    return;
                }
                const text = extractMeaningfulText(nestedValue, depth + 1);
                if (text) {
                    collected.push(text);
                }
            });

            if (collected.length) {
                return collected.join('\n');
            }

            if (decoded.code) {
                return String(decoded.code);
            }

            return '';
        };

        const noteValueRefs = ref<Record<number, HTMLElement>>({});
        const noteTruncated = ref<boolean[]>([]);

        const htmlToText = (value: any) => {
            if (value == null || value === '') {
                return '';
            }
            const container = document.createElement('div');
            container.innerHTML = String(value);
            return (container.textContent || '').replace(/\s+/g, ' ').trim();
        };

        const noteTypeLabels: Record<string, string> = {
            NOTE: 'message.NOTE',
            PO_NUMBER: 'message.PO_NUMBER',
            DELIVERY_INSTRUCTION: 'message.DELIVERY_INSTRUCTION'
        };

        const orderNoteLabel = (noteType: string) => {
            const translationKey = noteTypeLabels[String(noteType || '').toUpperCase()];
            return translationKey ? i18n.t(translationKey) : noteType;
        };

        const orderNotesList = computed(() => {
            const notes = orders.value?.order_notes;
            if (!Array.isArray(notes)) {
                return [];
            }
            return notes.map(item => ({
                label: orderNoteLabel(item?.note_type),
                value: htmlToText(item?.note)
            }));
        });

        const setNoteValueRef = (el: any, index: number) => {
            if (el) {
                noteValueRefs.value[index] = el as HTMLElement;
            } else {
                delete noteValueRefs.value[index];
            }
        };

        const updateNoteTruncation = () => {
            noteTruncated.value = orderNotesList.value.map((note, index) => {
                const el = noteValueRefs.value[index];
                return el ? el.scrollWidth > el.clientWidth + 1 : false;
            });
        };

        watch(orderNotesList, () => nextTick(updateNoteTruncation));

        const isOrderSentToBc = computed(() => {
            const number = orders.value?.economic_order_number;
            return number != null && String(number).trim() !== '';
        });

        const bcErrorMessage = computed(() => {
            const remoteResponse = orders.value?.remote_response;
            if (remoteResponse == null || remoteResponse === '') {
                return '';
            }

            const message = extractMeaningfulText(remoteResponse);
            if (!message) {
                return '';
            }

            const lines = message
                .split('\n')
                .map(line => (isGenericMessage(line) ? i18n.t('message.BUSINESS_CENTRAL_API_POST_ERROR') : line.trim()))
                .filter(Boolean);

            return Array.from(new Set(lines)).join('\n');
        });

        const updateStatus = async () => {
            loader.value = true;
            await Apollo.mutate({
                mutation: UPDATE_ORDER_STATUS,
                variables: { id: orders.value.id, order_status_id: order_status.value },
                update: (store, { data: { update_order_status } }) => {
                    loader.value = false;
                    Notify.success(`${i18n.t('message.RECORD_UPDATED_SUCCESSFULLY')}`);
                }
            }).catch(() => {
                loader.value = false;
            });
        };

        const orderDetails = uuid => {
            loader.value = true;
            Apollo.watchQuery({
                query: GET_ORDER_DETAIL,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-only',
                variables: {
                    uuid: uuid
                }
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                orders.value = data.order_detail;
                customer.value = data?.order_detail?.customer;
                orderTotals.value = {};
                if (orders.value.order_total.length > 0) {
                    orders.value.order_total.forEach(element => {
                        orderTotals.value[element.code] = {
                            title: element.title,
                            value: element.value
                        };
                    });
                }
                loader.value = false;
            });
        };

        const orderStatus = () => {
            // loader.value = true;
            Apollo.watchQuery({
                query: GET_ORDER_STATUS,
                errorPolicy: 'all',
                fetchPolicy: 'network-only',
                nextFetchPolicy: 'cache-only'
            }).subscribe(({ data, errors }) => {
                if (errors) {
                    loader.value = false;
                }
                if (data?.orders_status.length > 0) {
                    orderStatusList.value = JSON.parse(data?.orders_status);
                }

                // loader.value = false;
            });
        };

        const resendMsg = order_id => {
            try {
                sendMailLoader.value = true;
                Apollo.watchQuery({
                    query: RESEND_EMAIL,
                    errorPolicy: 'all',
                    fetchPolicy: 'network-only',
                    nextFetchPolicy: 'cache-only',
                    variables: {
                        order_id: order_id
                    }
                }).subscribe(({ errors }) => {
                    if (errors) {
                        sendMailLoader.value = false;
                    }
                    sendMailLoader.value = false;
                    Notify.success(`${i18n.t('message.MESSAGE_SENT')}`);
                });
            } catch (e) {
                sendMailLoader.value = false;
                Notify.error(i18n.t('message.SOMETHING_WENT_WRONG'));
            }
        };

        const resendOrderEconomic = async order_id => {
            if (!order_id || loader.value) {
                return;
            }
            loader.value = true;
            try {
                await Axios.post('/sap/orders/' + order_id);
                Notify.success(i18n.t('message.SAP_ORDER_RESEND_SUCCESSFULLY'));
                orderDetails(route.params.uuid);
            } catch ($e) {
                Notify.error(($e as any)?.response?.data?.message ?? i18n.t('message.SOMETHING_WENT_WRONG'));
                orderDetails(route.params.uuid);
            }
        };

        const clearPageHistory = () => {
            setPageHistory({});
        };
        onMounted(() => {
            orderDetails(route.params.uuid);
            orderStatus();
            window.addEventListener('resize', updateNoteTruncation);
        });

        onUnmounted(() => {
            window.removeEventListener('resize', updateNoteTruncation);
        });

        return {
            orders,
            me,
            customer,
            orderTotals,
            system_locale,
            columns,
            loader,
            clearPageHistory,
            companyLogo,
            i18n,
            asideTheme,
            resendMsg,
            resendOrderEconomic,
            sendMailLoader,
            updateStatus,
            statusList,
            order_status,
            defaultImage,
            bcErrorMessage,
            isOrderSentToBc,
            orderNotesList,
            noteTruncated,
            setNoteValueRef
        };
    }
});
